import React, { FC, useEffect } from "react";
import { useQuery } from "@apollo/client";

import {
  GetSubjectsQuery,
  GetSubjectsQueryVariables,
  SchemaReference,
} from "src/lib/graphql/types";
import SubjectDropdown, {
  schemaDropdownValue,
} from "src/components/SubjectDropdown";
import { subjectsQuery } from "src/lib/graphql/schemas.queries";
import LoadingIndicator from "src/components/general/LoadingIndicator";

type SchemaReferenceDropdownProps = {
  onChange: (newValues: SchemaReference[]) => void;
  required?: boolean;
  value?: SchemaReference[];
  defaultValue?: {
    name: string;
    namespace: string;
  };
};

const SchemaReferenceDropdown: FC<SchemaReferenceDropdownProps> = ({
  onChange,
  required,
  value,
  defaultValue,
}) => {
  const { data: subjectsData, loading } = useQuery<
    GetSubjectsQuery,
    GetSubjectsQueryVariables
  >(subjectsQuery);

  const updateSchemaReferences = (stringValues: string[]) =>
    onChange(
      stringValues?.map((value) => {
        const [namespace, name, version] = value.slice(1).split("/");
        return {
          namespace,
          name,
          version,
        };
      })
    );

  const defaultSubject = (subjectsData?.subjects ?? []).find(
    (subject: SchemaReference) =>
      subject.name === defaultValue?.name &&
      subject.namespace === defaultValue?.namespace
  );

  useEffect(() => {
    if (defaultSubject) {
      onChange([
        ...(value ?? []),
        {
          name: defaultSubject.name,
          namespace: defaultSubject.namespace,
          version: defaultSubject.version,
        },
      ]);
    }
  }, [defaultSubject]);

  return loading ? (
    <LoadingIndicator loading />
  ) : (
    <SubjectDropdown
      id="schema-reference-dropdown"
      isMulti
      largeList
      onChange={(newValues) => updateSchemaReferences(newValues as string[])}
      options={subjectsData?.subjects ?? []}
      placeholder={"Select type(s)"}
      required={required}
      value={value?.map((type) => schemaDropdownValue(type, true))}
      withVersion
    />
  );
};

export default SchemaReferenceDropdown;
